import { CadenceType } from "@models/Cadence";
import { requestActions } from "./apiHelpers";
import { catchError, delay, map, mergeMap, Observable, of, startWith } from "rxjs";
import { ReduxAction } from "@app/types";
import { combineEpics, ofType, StateObservable } from "redux-observable";
import { AppState } from "@reducers/rootReducer";
import { apiObservable } from "@support/api";
import Occasion from "@support/Occasion";
import { fetchGroups, selectGroup, selectGroupCollection } from "./groups";
import { chunkArray } from "@support/array";

const csvImportActions = requestActions('CSV_IMPORT', ['create']);
const csvImportUploadActions = requestActions('CSV_IMPORT_UPLOAD', ['create']);
const importStatusActions = requestActions('IMPORT_STATUS', ['fetch'])

export interface DateParts { year?: number; month: number; day: number; }
interface WorkExperience { title?: string; company?: string };
interface Address { street?: string; city?: string; state?: string; postal_code?: string; country?: string };

export interface ImportablePerson {
  // Lookup fields. Need to have at least one of these (or both First Name and Last Name together) for a valid row
  'name'?: string;
  'first_name'?: string;
  'last_name'?: string;
  'email_addresses'?: string[];
  'phone_numbers'?: string[];
  'linkedin_urls'?: string[];
  'facebook_urls'?: string[];
  'twitter_urls'?: string[];

  'notes'?: string[];
  'groups'?: string[];
  'birthday'?: DateParts;
  'work_history'?: WorkExperience[];
  'websites'?: string[];
  'headline'?: string;
  'cadence'?: CadenceType;
  'addresses'?: Address[];
}

export const createCSVImport = ({ rows, group_names }: {
  rows: ImportablePerson[];
  group_names: string[];
}) => csvImportActions.create({ rows, group_names });

const createCSVEpic = (
    action$: Observable<ReduxAction>,
    state$: StateObservable<AppState>,
) => action$.pipe(
  ofType('CREATE_CSV_IMPORT'),
  mergeMap(({ payload }) => {
    const dateDisplay = (new Occasion()).format('y-MM-dd');
    let name = `Import - ${dateDisplay}`
    const groups = selectGroupCollection(state$.value).items.valueSeq().toArray();
    const groupsImportedToday = groups.filter((group) => group.title.startsWith(name));

    if (groupsImportedToday.length > 0) {
      name = `${name} (${groupsImportedToday.length})`;
    }

    return apiObservable(
    'POST',
    '/integrations/bulk-import/',
    {
      name,
      total: payload.rows.length,
      group_names: payload.group_names,
    },
    { apiVersion: 'v2' },
  ).pipe(
    map(({ response }) => csvImportActions.createSucceeded({ ...response, rows: payload.rows })),
    startWith(csvImportActions.createRequested(payload)),
    catchError((err) => of(csvImportActions.createFailed(err))),
  )
})
);

const uploadImportEpic = (action$: Observable<ReduxAction>) => action$.pipe(
  ofType('CREATE_CSV_IMPORT_SUCCEEDED'),
  mergeMap(({ payload }) => {
    // for now we will chunk by 1,000. we can get more sophisticated with https://github.com/miktam/sizeof if we need to
    const chunks = chunkArray<ImportablePerson>(payload.rows, 1000);

    if (!chunks || !payload.uuid) {
      return of({ type: 'NO_OP' });
    }

    return of(...chunks.map((contacts) => csvImportUploadActions.create({ importId: payload.uuid, contacts })));
  }),
)

const uploadImportChunkEpic = (action$: Observable<ReduxAction>) => action$.pipe(
  ofType('CREATE_CSV_IMPORT_UPLOAD'),
  mergeMap(({ payload }) => apiObservable(
      'POST',
      `/integrations/bulk-import/${payload.importId}/contacts/`,
      { contacts: payload.contacts },
      { apiVersion: 'v2' },
    ).pipe(
      map(({ response }) => csvImportUploadActions.createSucceeded(response)),
      startWith(csvImportUploadActions.createRequested(payload)),
      // catchError((err) => of(csvImportUploadActions.createFailed(err))),
      // retry(3),
    )
  ));

const updateCSVGroupsEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> => action$.pipe(
  ofType('CREATE_CSV_IMPORT_SUCCEEDED'),
  map(() => fetchGroups({ omitContactIDs: true, isBackgroundSync: true })))


// when groups have been fetched, poll for import status on any group that has an importStatus that is not 'complete'
// on 'complete', do a background sync fetch of all groups for nav
const startPollingForImportStatusEpic = (action$: Observable<ReduxAction>): Observable<ReduxAction> => action$.pipe(
  ofType('FETCH_ALL_GROUPS_SUCCEEDED'),
  mergeMap(({ payload }) => {
    const uuidsToPoll: string[][] = payload.response?.filter((group) => !!group.bulk_import?.status && group.bulk_import.status !== 'complete').map((group) => [group.bulk_import.uuid, String(group.id)]) || [];

    if (uuidsToPoll.length === 0) {
      return of({ type: 'NO_OP' });
    }

    return of(...uuidsToPoll.map(([uuid, groupID]) => importStatusActions.fetch({ uuid, groupID })))
  }),
)


// should stop doing this if the import is 'complete' OR if the associated group is deleted
const pollForImportStatusEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) => action$.pipe(
  ofType('FETCH_IMPORT_STATUS'),
  mergeMap(({ payload }) => {
    // If the group associated with an import has been deleted, stop polling
    const group = selectGroup(payload.groupID, null)(state$.value);

    return apiObservable(
      'GET',
      `/integrations/bulk-import/${payload.uuid}/`,
      {},
      { apiVersion: 'v2' },
    ).pipe(
      delay(1000 * 15), // every 15 seconds
      mergeMap(({ response }) => {
        if (!group) {
          return of({ type: 'NO_OP' });
        }

        if (response?.status && response.status !== 'completed') {
          return of(importStatusActions.fetch({ uuid: payload.uuid, groupID: payload.groupID }))
        } else {
          return of(importStatusActions.fetchSucceeded(response))
        }
      }),
    )
  })
)

export const csvImportEpics = combineEpics(
  createCSVEpic,
  uploadImportEpic,
  uploadImportChunkEpic,
  updateCSVGroupsEpic,
  startPollingForImportStatusEpic,
  pollForImportStatusEpic,
)
